// @flow
import * as React from 'react';
import { defineMessages, injectIntl, IntlShape } from 'react-intl';
import styled from 'styled-components';
import * as Yup from 'yup';
import { CoordinatesPicker } from 'speed-js-react/src/components/coordinatespicker';
import { Box } from 'grommet';
import NumberInput from 'speed-js-react-web/src/components/fields/NumberInput';
import FieldWrapper from './FieldWrapper';


type Props = {
    field: {
        onChange: (e: SyntheticInputEvent<number>) => void,
        onFocus: (e: SyntheticFocusEvent<HTMLInputElement>) => void,
        onBlur: (e: SyntheticFocusEvent<any>) => void,
        value: any;
        name: string;
    },
    form: {
        errors: Object,
        touched: Object,
        isSubmitting: boolean,
        setFieldValue: Function,
    },
    required: boolean,
    label: string,
    help_text: string,
    disabled?: boolean,
    intl: IntlShape,
    step?: number,
}

const LatInput = styled(NumberInput)`
    width: 95%;
    margin-left: 5%;
`;

const LngInput = styled(NumberInput)`
   width: 95%;
`;

const CoordinatesInput = (props: Props) => {
    const {
        disabled,
        field: {
            name, onBlur, value,
        },
        field,
        form: {
            isSubmitting, setFieldValue,
        },
        label,
        help_text: help,
        required,
        intl: { formatMessage },
        step,
    } = props;

    const handleChange = (event, val, inputIndex) => {
        if (inputIndex) {
            setFieldValue(name, {
                ...value,
                coordinates: value.coordinates.map((coords, i) => {
                    if (i === inputIndex) {
                        return val.split(',').reverse().map((v) => {
                            if (!v) {
                                return '';
                            }
                            return parseFloat(v);
                        });
                    }
                    return coords;
                }),
            });
            return;
        }

        setFieldValue(name, {
            ...value,
            coordinates: val.split(',').reverse().map((v) => {
                if (!v) {
                    return '';
                }
                return parseFloat(v);
            }),
        });
    };

    const componentProps = { type: 'number', disabled: disabled || isSubmitting, step };

    const allowedDigits = step ? step.toString().split('.')[1].length : 5;

    return (
        <FieldWrapper
            name={name}
            required={required}
            helpText={help}
            label={label}
        >
            <Box direction="row" gap="medium" justify="between">
                {value && value.coordinates.every((v) => Array.isArray(v)) ? value.coordinates.map((v, idx) => (
                    <CoordinatesPicker
                        lat={LatInput}
                        lng={LngInput}
                        latValue={v[1]}
                        lngValue={v[0]}
                        field={field}
                        onBlur={onBlur}
                        onChange={(event, values) => handleChange(event, values, idx)}
                        latComponentProps={componentProps}
                        lngComponentProps={componentProps}
                        coordinates={{ coordinates: [v.coordinates[1], v.coordinates[0]] }}
                    />
                )) : (
                    <CoordinatesPicker
                        lat={LatInput}
                        lng={LngInput}
                        latValue={value?.coordinates[1] || 0}
                        lngValue={value?.coordinates[0] || 0}
                        field={field}
                        onBlur={onBlur}
                        onChange={handleChange}
                        latComponentProps={componentProps}
                        lngComponentProps={componentProps}
                        coordinates={{ coordinates: [value?.coordinates[1] || 0, value?.coordinates[0] || 0] }}
                    />
                )}
            </Box>
        </FieldWrapper>
    );
};

CoordinatesInput.defaultProps = {
    disabled: false,
    step: undefined,
};

CoordinatesInput.getValidationSchema = (field: { validation: Object }) => {
    if (field.validation.required) {
        return Yup.string().required('The field is required').nullable();
    }

    return Yup.string().nullable();
};

export default injectIntl(CoordinatesInput);
